<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between gap-4">
      <p class="max-w-prose text-sm text-obsidian-500">{{ t('libraryHint') }}</p>
      <CVButton v-if="$can('create', 'Media')" variant="default" @click="handleEditMedia(true)">
        <PlusIcon class="size-4" /> {{ t('addMedia') }}
      </CVButton>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner class="text-hazel-600" />
    </div>

    <EntityListExceptions
      v-else
      :status="status"
      :error="error as any"
      :items-count="mediaItems.length"
      :no-items-text="t('noMediaFound')"
    />

    <MediaLibraryGrid v-if="mediaItems.length" :items="mediaItems" @select="handleOpenMedia" />

    <div v-if="hasNextPage" class="flex justify-center">
      <CVButton outline :is-loading="isFetchingNextPage" @click="fetchNextPage">
        {{ t('loadMore') }}
      </CVButton>
    </div>

    <Modal v-model="showModal" :content-class="'!max-w-5xl w-full'" :show-close="true">
      <MediaPlayer
        v-if="activeMediaId && !isEditMode"
        :media-id="activeMediaId"
        @edit="handleEditMedia()"
        @delete="handleDelete()"
      />
      <EditMedia
        v-else-if="isEditMode && activeMediaId"
        :media-id="activeMediaId"
        @saved="handleCloseEdit"
        @cancel="handleCloseEdit"
      />
      <MediaForm v-else-if="isCreateMode" @saved="handleCreated" @cancel="handleCloseEdit" />
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import MediaLibraryGrid from '@/components/media/MediaLibraryGrid.vue';
import { useDeleteMedia, useMediaList } from '@/queries/use-media';
import { useMediaListStatusSync } from '@/queries/use-media-list-status-sync';
import type { Media as RosMedia } from '@carvia/ros-client-types';
import { Order } from '@carvia/ros-client-types';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import MediaPlayer from './MediaPlayer.vue';
import EditMedia from './EditMedia.vue';
import MediaForm from './MediaForm.vue';
import { Alert } from '@/services/alert.service';
import router from '@/router';
import { useRoute } from 'vue-router';
import { PlusIcon } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = useRoute();

const { data, isLoading, status, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
  useMediaList({ limit: 9, order: Order.DESC });
const mediaItems = useFlattenPaginatedData(data);
const { mutateAsync: deleteMedia } = useDeleteMedia();

useMediaListStatusSync();

const showModal = ref(false);
const activeMediaId = ref<string | null>(null);
const isEditMode = ref(false);
const isCreateMode = ref(false);

const queryWithoutMedia = () => {
  const { id: _ignored, ...rest } = route.query;
  return rest;
};

function handleOpenMedia(item: RosMedia) {
  isEditMode.value = false;
  isCreateMode.value = false;
  activeMediaId.value = item.id;
  showModal.value = true;
  router.replace({ name: 'media', query: { ...route.query, id: item.id } });
}

function handleEditMedia(createNew = false) {
  isEditMode.value = !createNew;
  isCreateMode.value = createNew;
  activeMediaId.value = createNew ? null : activeMediaId.value;
  showModal.value = true;
}

function handleCreated(media: RosMedia) {
  isCreateMode.value = false;
  activeMediaId.value = media.id;
}

async function handleDelete() {
  if (!activeMediaId.value) return;
  const { isConfirmed } = await Alert.fire({
    titleText: t('confirmDeleteMedia'),
    icon: 'warning',
    buttons: [
      {
        text: t('delete'),
        style: 'danger',
        propertyName: 'isConfirmed',
      },
      {
        text: t('cancel'),
        style: 'ghost',
        propertyName: 'isDismissed',
      },
    ],
  });
  if (!isConfirmed) return;
  await deleteMedia(activeMediaId.value);
  activeMediaId.value = null;
  showModal.value = false;
}

function handleCloseEdit() {
  isEditMode.value = false;
  isCreateMode.value = false;
  if (!activeMediaId.value) {
    showModal.value = false;
  }
}

watch(showModal, (newVal) => {
  if (!newVal) {
    router.replace({ name: 'media', query: queryWithoutMedia() });
  }
});

watch(
  () => route.query.id,
  (id) => {
    if (typeof id !== 'string' || !id) return;
    isEditMode.value = false;
    isCreateMode.value = false;
    activeMediaId.value = id;
    showModal.value = true;
  },
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "libraryHint": "Upload and manage the videos and images available across CarVia.",
    "noMediaFound": "No media found.",
    "addMedia": "Add video",
    "confirmDeleteMedia": "Do you really want to delete this media item?",
    "delete": "Delete",
    "cancel": "Cancel",
    "loadMore": "Load more"
  },
  "de": {
    "libraryHint": "Lade die Videos und Bilder hoch, die in CarVia verwendet werden, und verwalte sie.",
    "noMediaFound": "Keine Medien gefunden.",
    "addMedia": "Video hinzufügen",
    "confirmDeleteMedia": "Möchtest du dieses Medium wirklich löschen?",
    "delete": "Löschen",
    "cancel": "Abbrechen",
    "loadMore": "Mehr laden"
  },
  "es": {
    "libraryHint": "Sube y gestiona los vídeos e imágenes disponibles en CarVia.",
    "noMediaFound": "No se encontraron medios.",
    "addMedia": "Añadir vídeo",
    "confirmDeleteMedia": "¿Seguro que desea eliminar este medio?",
    "delete": "Eliminar",
    "cancel": "Cancelar",
    "loadMore": "Cargar más"
  }
}
</i18n>
